import React, { useState } from "react";
import styled from "styled-components";
import BackdropWrapper from "./modals/BackdropWrapper";
import ActionModal from "./modals/ActionModal";
import { useNavigate } from "react-router";
import { auth, db } from "../authentication/firebase";
import { addDoc, collection, onSnapshot } from "firebase/firestore";
import LoadingButton from "./LoadingButton";
import Info from "./modals/Info";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";

const PricingCard = ({ priceInfo }) => {
  const plan = priceInfo.productData;
  const [actionModal, setActionModal] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState({
    active: false,
    error: "",
  });

  const subscription = useSelector((state) => state.user.subscription);

  const navigate = useNavigate();

  // Check if user is subscribed to this specific plan
  const planPriceId = priceInfo.id;
  const subscribedPriceId = subscription?.items?.[0]?.price?.id;
  const isCurrentPlan =
    planPriceId === subscribedPriceId && subscription?.status === "active";

  // Get subscription details for display
  const subscriptionStartDate = subscription?.current_period_start
    ? new Date(
        subscription.current_period_start.seconds * 1000
      ).toLocaleDateString()
    : null;

  const subscriptionEndDate = subscription?.current_period_end
    ? new Date(
        subscription.current_period_end.seconds * 1000
      ).toLocaleDateString()
    : null;

  const isCanceled = subscription?.cancel_at_period_end === true;

  const upgradeHandler = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      backdropHandler();
      return;
    }
    setSubscriptionLoading(true);
    try {
      const collectionRef = collection(
        db,
        "customers",
        uid,
        "checkout_sessions"
      );

      const docRef = await addDoc(collectionRef, {
        price: priceInfo.id,
        success_url: `${window.location.origin}/payment-successful`,
        cancel_url: `${window.location.origin}/subscribe?error=true`,
      });

      onSnapshot(docRef, (snap) => {
        const { error, url } = snap?.data();
        if (error) {
          setSubscriptionError({ active: true, error: error.message });
          setSubscriptionLoading(false);
        } else if (url) {
          window.location.assign(url);
        }
      });
    } catch (e) {
      setSubscriptionLoading(false);
      console.log("error while subscription redirecting", e);
    }
  };

  const backdropHandler = () => {
    setActionModal((current) => !current);
  };

  const actionModalFunction = () => {
    backdropHandler();
    navigate("/login", { state: { from: "/subscribe" } });
  };

  const subscriptionBackdropHandler = () => {
    setSubscriptionError((current) => ({
      ...current,
      active: !current.active,
    }));
  };

  return (
    <>
      <BackdropWrapper
        open={actionModal}
        smallSize={true}
        backdropHandler={backdropHandler}
        element={
          <ActionModal
            heading={"Login Required"}
            msg={"Please login or signup before upgrading"}
            backdropHandler={backdropHandler}
            buttonName={"Login"}
            action={actionModalFunction}
          />
        }
      />
      <BackdropWrapper
        open={subscriptionError.active}
        smallSize={true}
        backdropHandler={subscriptionBackdropHandler}
        element={
          <Info
            heading={"Subscription Error"}
            msg={"Please contact us to solve this issue"}
            backdropHandler={subscriptionBackdropHandler}
          />
        }
      />
      <StyledPricingCard
        featured={plan?.featured && !isCurrentPlan}
        isCurrentPlan={isCurrentPlan}
      >
        <div>
          <h2>{plan.name}</h2>
          <div className="price">
            <span className="amount">${priceInfo?.unit_amount / 100}</span>
            <span className="period">/{priceInfo?.interval}</span>
          </div>
          <p className="plan-for">{plan.for}</p>
        </div>

        <ul className="benefits">
          {plan.benefits?.map((benefit, idx) => (
            <li key={idx}>
              <CheckCircleOutlineIcon className="check-icon" />
              {benefit}
            </li>
          ))}
        </ul>
        {plan.bgImg && (
          <img className="bg-img" src={plan.bgImg} alt={plan.name} />
        )}

        {isCurrentPlan ? (
          <CurrentPlanSection>
            <CurrentPlanBadge>Current Plan</CurrentPlanBadge>
            <SubscriptionDetails>
              <DetailItem>
                <span className="label">Started:</span>
                <span className="value">{subscriptionStartDate}</span>
              </DetailItem>
              <DetailItem>
                <span className="label">Renews:</span>
                <span className="value">{subscriptionEndDate}</span>
              </DetailItem>
              {isCanceled && (
                <CancelledNotice>
                  Subscription cancelled - active until {subscriptionEndDate}
                </CancelledNotice>
              )}
            </SubscriptionDetails>
            {/* <ManageButton onClick={() => navigate('/subscription-detail')}>
              Manage Subscription
            </ManageButton> */}
          </CurrentPlanSection>
        ) : (
          <LoadingButton
            loading={subscriptionLoading}
            onClick={upgradeHandler}
            className={plan?.featured ? "featured" : ""}
            style={{ zIndex: 1 }}
          >
            Subscribe
          </LoadingButton>
        )}
      </StyledPricingCard>
    </>
  );
};

const StyledPricingCard = styled.div`
  overflow: hidden;
  width: 100%;
  max-width: 350px;
  border-radius: 24px;
  padding: 32px;
  background: #fff;
  border: ${(props) => {
    if (props.isCurrentPlan) return "2px solid transparent";
    else if (props.featured) return "2px solid var(--primary-color)";
    else return "1px solid rgba(0, 0, 0, 0.08)";
  }};
  background-image: ${(props) =>
    props.isCurrentPlan
      ? "linear-gradient(white, white), linear-gradient(135deg, #97aafd 0%, #e971ff 100%)"
      : "none"};
  background-origin: ${(props) =>
    props.isCurrentPlan ? "border-box" : "initial"};
  background-clip: ${(props) =>
    props.isCurrentPlan ? "padding-box, border-box" : "initial"};
  display: flex;
  flex-direction: column;
  gap: 32px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  }

  h2 {
    font-size: 28px;
    color: #333;
    margin: 0;
    font-weight: 600;
  }

  .bg-img {
    position: absolute;
    bottom: 0;
    right: -5%;
    max-width: 150px;
    z-index: 0;
  }

  .price {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 8px;

    .amount {
      font-size: 42px;
      font-weight: bold;
      color: #333;
    }

    .period {
      font-size: 18px;
      color: #666;
    }
  }

  .plan-for {
    color: #666;
    font-size: 18px;
    margin: 0;
  }

  .benefits {
    list-style-type: none;
    padding: 0;
    margin: 24px 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 200px;
    li {
      display: flex;
      align-items: center;
      color: #444;
      font-size: 16px;
      line-height: 1.5;

      .check-icon {
        color: var(--primary-dark-color);
        margin-right: 12px;
        font-size: 20px;
      }
    }
  }

  button {
    width: 100%;
    padding: 16px;
    border-radius: 100px;
    border: none;
    background-color: ${(props) =>
      props.featured ? "var(--primary-color)" : "#f5f5f5"};
    color: ${(props) => (props.featured ? "#fff" : "#333")};
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${(props) =>
        props.featured ? "var(--primary-dark-color)" : "#ebebeb"};
    }
  }

  ${(props) =>
    props.featured &&
    `
    &::before {
      content: '';
      position: absolute;
      top: -2%;
      right: -2%;
      width: 100px;
      height: 100px;
      background: #A6D96A;
      clip-path: polygon(0 0, 100% 0, 100% 100%);
      border-top-right-radius: 24px;
    }
  `}
`;

const CurrentPlanSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CurrentPlanBadge = styled.div`
  background: linear-gradient(135deg, #97aafd 0%, #e971ff 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SubscriptionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;

  .label {
    color: #666;
    font-weight: 500;
  }

  .value {
    color: #333;
    font-weight: 600;
  }
`;

const CancelledNotice = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  border-left: 3px solid #ffeeba;
`;

const ManageButton = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 100px;
  border: 2px solid transparent;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  /* &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  } */
`;

export default PricingCard;
